import type { StreamingChatId } from "@storytell/streaming";
import { batch } from "solid-js";
import { createStore, produce } from "solid-js/store";
import { isServer } from "solid-js/web";

/**
 * Maximum number of chats to track expanded state for.
 * When exceeded, oldest entries are removed.
 */
const MAX_TRACKED_CHATS = 20;

const STORAGE_KEY = "storytell:expanded-state";

interface PersistedState {
  expandedState: Record<string, Record<string, boolean>>;
  manuallyToggled: Record<string, Record<string, boolean>>;
  chatAccessOrder: string[];
}

let saveTimeout: ReturnType<typeof setTimeout> | null = null;

const loadFromStorage = (): PersistedState | null => {
  if (isServer) return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as PersistedState;
    if (
      typeof parsed.expandedState === "object" &&
      typeof parsed.manuallyToggled === "object" &&
      Array.isArray(parsed.chatAccessOrder)
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

// Track the order chats were accessed for LRU cleanup
const chatAccessOrder: string[] = [];

/**
 * Persistent store for tracking expanded/collapsed state of sections in chat messages.
 * State is keyed by chatId and a unique section identifier (messageId or operationId).
 * This ensures that when content streams in and components re-render, the user's
 * expanded/collapsed preferences are preserved.
 */
const initialState = loadFromStorage();
const [expandedState, setExpandedState] = createStore<Record<string, Record<string, boolean>>>(
  initialState?.expandedState ?? {}
);
// Track which sections have been manually toggled by the user
const [manuallyToggled, setManuallyToggled] = createStore<Record<string, Record<string, boolean>>>(
  initialState?.manuallyToggled ?? {}
);

// Initialize chatAccessOrder from localStorage
if (initialState?.chatAccessOrder) {
  chatAccessOrder.push(...initialState.chatAccessOrder);
}

const saveToStorage = () => {
  if (isServer) return;

  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  saveTimeout = setTimeout(() => {
    try {
      const state: PersistedState = {
        expandedState: { ...expandedState },
        manuallyToggled: { ...manuallyToggled },
        chatAccessOrder: [...chatAccessOrder],
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage errors (quota exceeded, etc.)
    }
  }, 500);
};

/**
 * Update chat access order and perform LRU cleanup if needed.
 */
const touchChat = (chatId: string) => {
  // Remove from current position if exists
  const existingIndex = chatAccessOrder.indexOf(chatId);
  if (existingIndex > -1) {
    chatAccessOrder.splice(existingIndex, 1);
  }

  // Add to end (most recently accessed)
  chatAccessOrder.push(chatId);

  // If over limit, remove oldest chat's state
  while (chatAccessOrder.length > MAX_TRACKED_CHATS) {
    const oldestChatId = chatAccessOrder.shift();
    if (oldestChatId) {
      setExpandedState(
        produce((state) => {
          delete state[oldestChatId];
        })
      );
      setManuallyToggled(
        produce((state) => {
          delete state[oldestChatId];
        })
      );
    }
  }
};

/**
 * Hook to manage persistent expanded state for a section in a chat thread.
 *
 * @param chatId - The chat thread ID
 * @param sectionId - Unique identifier for the section (e.g., messageId, operationId, or custom key)
 * @param defaultExpanded - Default expanded state if not previously set
 * @returns A tuple of [isExpanded, setIsExpanded, hasBeenManuallyToggled]
 */
export const usePersistentExpandedState = (
  chatId: StreamingChatId,
  sectionId: string,
  defaultExpanded = false
): [() => boolean, (expanded: boolean, isManual?: boolean) => void, () => boolean] => {
  // Track chat access for LRU cleanup
  touchChat(chatId);

  // Get current state or use default
  // Only return persisted state if the section was manually toggled
  // This prevents storing state for every operation (which could grow unbounded)
  const isExpanded = () => {
    // If manually toggled, use persisted state; otherwise use default
    if (manuallyToggled[chatId]?.[sectionId]) {
      return expandedState[chatId]?.[sectionId] ?? defaultExpanded;
    }
    return defaultExpanded;
  };

  // Check if this section has been manually toggled by the user
  const hasBeenManuallyToggled = () => {
    return manuallyToggled[chatId]?.[sectionId] ?? false;
  };

  // Set state - ensure nested structure exists before setting
  const setIsExpanded = (expanded: boolean, isManual = false) => {
    // CRITICAL: If this is a manual toggle, set the manuallyToggled flag FIRST (synchronously)
    // This ensures that when createEffect runs (triggered by expandedState change),
    // hasBeenManuallyToggled() will already return true
    if (isManual) {
      // If the chatId doesn't exist in the manuallyToggled store, initialize it
      if (!manuallyToggled[chatId]) {
        setManuallyToggled(chatId, {});
      }
      setManuallyToggled(chatId, sectionId, true);

      // Only persist expanded state for manually toggled sections to prevent unbounded growth
      // Auto-collapsed/expanded states are not persisted - they use the default
      batch(() => {
        // If the chatId doesn't exist in the store, initialize it
        if (!expandedState[chatId]) {
          setExpandedState(chatId, {});
        }
        // Now set the sectionId value
        setExpandedState(chatId, sectionId, expanded);
      });

      saveToStorage();
    } else {
      // For auto-collapsed/expanded states, don't persist them
      // They will use the default value on next render
      // This prevents unbounded growth of the store
    }
  };

  return [isExpanded, setIsExpanded, hasBeenManuallyToggled];
};
